/**
 * Quality Co-Pilot — the general Merlin chat of the Quality Triage door
 * (`ask`, opened by the bar's "Ask Merlin" button since the tabs round): a
 * voice-first conversation for internal staff.
 *
 * You talk to it (push-to-talk → Whisper transcription) or type; it answers
 * with a SHORT spoken summary (auto-played via TTS, shown as a playable
 * bubble) AND a detailed written report. It runs the agent-console `recorder`
 * profile through the profile-locked `/api/quality-copilot` door, so it can
 * check orders, record quality signals, and answer questions about
 * customers/products — read-only on the data, signal writes to the KB.
 *
 * Phase 2 (kanban 12584b08): the conversation engine, the composer and the
 * transcript are the SHARED components under components/quality — the same
 * ones the split workspace mounts — so there is one Merlin chat, not two.
 * The 📨 Requests and 🔍 Investigations drawers, the in-chat case rail and
 * the triage-board overlay are gone from this tab (Scott, 2026-09-08: "the
 * investigations… overlaps in a weird way that's not intuitive"): cases live
 * on the Cases tab and open in the workspace; a conversation's own asks
 * render right here, above the composer.
 */
import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useOutletContext } from 'react-router-dom';

import ModelSelect from '../components/agentConsole/ModelSelect';
import {
  listConversations,
  renameConversation,
  submitQualityIssueForm,
  uploadAttachment,
  type ConversationRead,
  type QualityIssueAttachmentRef,
} from '../services/qualityCopilot';
import { useAuth } from '../context/AuthContext';
import { useGuruWidgetViewContext } from '../hooks/useGuruWidgetViewContext';
import { formatCostUsd } from '../services/agentConsole';
import { QualityCopilotComplaintForm } from './QualityCopilotComplaintForm';
import {
  complaintFormToPayload,
  composeComplaintPrompt,
  issueCategoryLabel,
  severityLabel,
  type ComplaintFormFields,
} from './qualityCopilotComplaint';
import { QcpConversationDrawer } from './QcpConversationDrawer';
import { AnswerReceivedBanner } from '../components/portal/AnswerReceivedBanner';
import { useQcpDataRequests } from '../components/portal/useQcpDataRequests';
import {
  SaveAsInvestigationMenu,
  type SaveOutcome,
} from '../components/portal/SaveAsInvestigationMenu';
import { SavedCaseNotice } from '../components/portal/SavedCaseNotice';
import { useQcpInvestigations } from '../components/portal/useQcpInvestigations';
import type { QualityTriageContext } from './quality-triage/qualityTriageContext';
import { fillCaseRequest } from './quality-triage/fillCaseRequest';
import {
  investigationForConversation,
  type DoorInvestigationRead,
} from '../services/qcpInvestigations';
import {
  GCP_VOICES,
  rememberedConversationId,
  useCopilotChat,
} from '../components/quality/useCopilotChat';
import { CopilotComposer } from '../components/quality/CopilotComposer';
import { CopilotTurnList } from '../components/quality/CopilotTurnList';
import { AsksSection } from '../components/quality/AsksSection';
import { primaryRef } from './quality-triage/caseIdentity';
import { trackEvent } from '../services/portalTracking';
import styles from './QualityCopilotPage.module.css';
import { useQualityReadOnly } from '../context/QualityReadOnlyContext';

export { CopilotTurnView } from '../components/quality/CopilotTurnView';

// The shared portal order modal, mounted in-conversation for Merlin drafts
// (self-fetches by orderId; lazy — the chunk only loads when a draft card's
// Review button is tapped, mirroring SplitScreenModal's pattern).
const OrderDetailModal = lazy(() => import('../components/portal/OrderDetailModal'));

// The structured "Form" intake is hidden for now (kept behind this flag so the
// code stays intact and can be flipped back on in one line). When false, the
// composer is the single voice+text bar only.
const SHOW_FORM_MODE = false;

interface Suggestion {
  emoji: string;
  label: string;
  /** Put in the message box to finish and send, never sent as it is: sent
   *  bare, Merlin took minutes only to ask for the detail it lacked
   *  (fan-out finding 7). Each ends where the reader types the specifics. */
  starter: string;
}

const SUGGESTIONS: Suggestion[] = [
  { emoji: '📦', label: 'Check an order', starter: 'Check the status of order ' },
  {
    emoji: '⚠️',
    label: 'Report a quality issue',
    starter: 'I need to report a quality issue. Customer, product and lot: ',
  },
  { emoji: '🏢', label: 'Ask about a customer', starter: 'Tell me about recent activity for customer ' },
];

/** "1.3×" / "1×" / "0.85×" — trims trailing zeros for the slider readout. */
function formatSpeed(rate: number): string {
  return `${rate.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}×`;
}

export default function QualityCopilotPage({
  embedded = false,
}: {
  /** True when mounted as the Quality Triage shell's Ask tab — the page gives
   *  up its own full-viewport height so the shell's tab bar stays visible. */
  embedded?: boolean;
} = {}) {
  const [inputMode, setInputMode] = useState<'voice' | 'text' | 'form'>('voice');
  // Arrived via the shell's "+ New quality issue": say how a report becomes a tracked case.
  const [reportIntent, setReportIntent] = useState(false);
  // The save menu is replaced by the "part of INV" pill the moment a case is
  // created, so the confirmation lives on the page, not in the menu.
  const [savedCase, setSavedCase] = useState<{
    investigation: DoorInvestigationRead;
    outcome: SaveOutcome;
    conversationId: string;
  } | null>(null);
  // A Merlin draft card's Review button opens the shared order modal here,
  // in-conversation (the "pop back with the order modal" flow).
  const [reviewOrderId, setReviewOrderId] = useState<string | null>(null);
  // Only admins see usage cost (tokens · $) — same as the Agent Console: the
  // per-turn line in each report AND the running conversation total below.
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  // A read-only viewer (kanban 808dabf6) chats but saves nothing as a case.
  const readOnly = useQualityReadOnly();

  // Per-user conversation history. The list is owner-scoped AND qcp-profile-
  // scoped server-side, so it only ever shows THIS user's Co-Pilot threads.
  const userId = user?.id ?? null;
  const ownerName = user?.name ?? null;
  const ownerInitials =
    (user?.name || user?.email || '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0])
      .join('') || null;
  const [conversations, setConversations] = useState<ConversationRead[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  // Refresh the history list (owner + qcp-profile scoped server-side).
  // Non-fatal: a failure just leaves the drawer showing what it had.
  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      setConversations(await listConversations({ limit: 50 }));
    } catch {
      /* history is best-effort; the live turn still works */
    } finally {
      setLoadingList(false);
    }
  }, []);

  // The shared conversation engine: this tab remembers the active thread per
  // user and creates one on the first send.
  const chat = useCopilotChat({
    userId,
    rememberActive: true,
    createOnDemand: true,
    onConversationCreated: () => void loadList(),
  });
  const activeId = chat.activeId;

  // Merlin's agent→human data requests: polled while the page is mounted
  // (30s, the notification convention). The ACTIVE conversation's asks render
  // above the composer; an unseen answer also raises the slim banner.
  const dataRequests = useQcpDataRequests(!!userId);
  const [asksForced, setAsksForced] = useState(false);
  // Jess's case files: polled alongside, for the header's save/"part of
  // INV-…" affordances. Hidden entirely when the deploy has the feature
  // flagged off (the endpoints 404).
  // Inside the Quality shell, share its case poll so the tab counts move
  // the moment a case is saved here (turn 2: "Cases" said 4 open until the
  // tab was reopened). Standalone, poll on our own.
  const shell = useOutletContext<QualityTriageContext | undefined>();
  const ownInvState = useQcpInvestigations(!!userId && !shell?.invState);
  const invState = shell?.invState ?? ownInvState;
  // The investigation the ACTIVE conversation belongs to (header pill →
  // opens that case's workspace on the Cases tab).
  const [askedToFill, setAskedToFill] = useState(false);
  const [convoInvestigation, setConvoInvestigation] =
    useState<DoorInvestigationRead | null>(null);
  const conversationTitleFor = useCallback(
    (conversationId: string | null) =>
      conversations.find((c) => c.id === conversationId)?.title ?? null,
    [conversations],
  );
  const conversationAsks = useMemo(
    () =>
      activeId
        ? dataRequests.requests.filter((r) => r.conversation_id === activeId)
        : [],
    [dataRequests.requests, activeId],
  );
  // The active conversation's first unseen answer (if any) → slim banner.
  const unseenActiveRequest = useMemo(
    () => conversationAsks.find((r) => r.answer_unseen) ?? null,
    [conversationAsks],
  );
  // Merlin can put the chat on a case mid-conversation (its case-file tools
  // attach it); the shared 30s poll carries each case's conversations, so
  // the "part of INV-…" pill follows without a reload (turn 1: the Save
  // button and its hint stayed after Merlin had filed the report on a case).
  const polledCase = useMemo(
    () =>
      activeId
        ? invState.investigations.find((i) => i.conversation_ids.includes(activeId)) ?? null
        : null,
    [activeId, invState.investigations],
  );
  const chatCase = convoInvestigation ?? polledCase;
  // The triage thread this chat is Merlin's conversation for, if any: with
  // no case yet the header names it by its QT number (proposal 6).
  const chatThread = useMemo(
    () => (activeId ? invState.threads.find((t) => t.conversation_id === activeId) ?? null : null),
    [activeId, invState.threads],
  );
  // On page load / conversation switch, resolve which investigation (if any)
  // the active conversation belongs to — populates the "part of INV-…" pill.
  useEffect(() => {
    setAsksForced(false);
    if (!activeId || !invState.available) {
      setConvoInvestigation(null);
      return;
    }
    let cancelled = false;
    investigationForConversation(activeId)
      .then((inv) => {
        if (!cancelled) setConvoInvestigation(inv);
      })
      .catch(() => {
        if (!cancelled) setConvoInvestigation(null);
      });
    return () => {
      cancelled = true;
    };
  }, [activeId, invState.available]);
  // The voice / speed / model controls collapse behind a ⚙ at every width.
  const [headerOpen, setHeaderOpen] = useState(false);
  const restoredRef = useRef(false);

  // Tell the Guru feedback widget which AI conversation is on screen (all
  // component state — nothing reaches the URL on this door).
  useGuruWidgetViewContext('quality_copilot_chat', {
    copilot: 'quality-copilot',
    conversation_id: activeId ?? undefined,
    conversation_title: conversations.find((c) => c.id === activeId)?.title ?? undefined,
  });

  const { loadConversation, resetConversation, setTextValue } = chat;

  // A starter chip fills the box and puts the cursor at its end.
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const putStarter = useCallback(
    (starter: string) => {
      setTextValue(starter);
      window.requestAnimationFrame(() => {
        const box = composerRef.current;
        if (!box) return;
        box.focus();
        box.setSelectionRange(starter.length, starter.length);
      });
    },
    [setTextValue],
  );

  // Resume a saved thread from the drawer.
  const openConversation = useCallback(
    (id: string) => {
      setDrawerOpen(false);
      void loadConversation(id);
    },
    [loadConversation],
  );

  // Rename a thread (optimistic): update the list immediately, then persist
  // via the shared rename endpoint; on failure, re-fetch to resync titles.
  const handleRename = useCallback(
    async (id: string, title: string) => {
      setConversations((prev) =>
        prev.map((c) => (c.id === id ? { ...c, title } : c)),
      );
      try {
        await renameConversation(id, title);
      } catch {
        void loadList();
      }
    },
    [loadList],
  );

  const startNew = useCallback(() => {
    resetConversation();
    setDrawerOpen(false);
  }, [resetConversation]);

  // Load the history list once the page mounts.
  useEffect(() => {
    void loadList();
  }, [loadList]);

  // Resume the last thread on (re)load: a ?conversation= deep-link wins, else
  // the per-user remembered id. Runs once auth is known (so we read the
  // right per-user key) and only once per mount.
  useEffect(() => {
    if (restoredRef.current || !userId) return;
    restoredRef.current = true;
    const params = new URLSearchParams(window.location.search);
    // The shell's "+ New quality issue" button lands here with an intent:
    // skip the resume (a report is a fresh thread). The effect below starts it.
    if (params.get('intent') === 'report-issue') return;
    const deepLink = params.get('conversation');
    const resumeId = deepLink || rememberedConversationId(userId);
    if (resumeId) void loadConversation(resumeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per mount, after auth
  }, [userId, loadConversation]);

  // "+ New quality issue" always starts a fresh conversation in report mode,
  // even when this tab is already open on another conversation: the intent
  // used to be read once per mount, so pressing it here did nothing (fan-out
  // finding 5). Each press is a new navigation (a new location key).
  const location = useLocation();
  const reportRequested = new URLSearchParams(location.search).get('intent') === 'report-issue';
  const handledReportKey = useRef<string | null>(null);
  useEffect(() => {
    if (!reportRequested || !userId || handledReportKey.current === location.key) return;
    handledReportKey.current = location.key;
    resetConversation();
    setReportIntent(true);
    setSavedCase(null);
    setAskedToFill(false);
    setConvoInvestigation(null);
    setDrawerOpen(false);
  }, [location.key, reportRequested, userId, resetConversation]);

  // The form-driven mode uploads any attached photos to the conversation
  // first, then composes the structured fields into one message and runs it
  // through the SAME submission flow voice/free-text use.
  const handleFormSubmit = useCallback(
    async (fields: ComplaintFormFields) => {
      await chat.withBusy(async () => {
        try {
          const { id: convoId } = await chat.ensureConversation();
          // Upload any photos first, keeping only the storage refs (the
          // structured payload forbids the upload response's extra fields).
          const refs: QualityIssueAttachmentRef[] = [];
          for (const photo of fields.photos) {
            const a = await uploadAttachment(convoId, photo);
            refs.push({
              filename: a.filename,
              gs_uri: a.gs_uri,
              mime_type: a.mime_type,
              size_bytes: a.size_bytes,
            });
          }
          // Write the STRUCTURED quality_issues row (typed, Sage-ready) —
          // NOT a free-text signal — via the deterministic form endpoint.
          const issue = await submitQualityIssueForm(
            complaintFormToPayload(fields, refs, convoId),
          );
          // Echo what was filed + a confirmation into the conversation.
          const userTurnId = chat.nextId();
          const asstTurnId = chat.nextId();
          const filedAt = new Date().toISOString();
          chat.setTurns((prev) => [
            ...prev,
            {
              id: userTurnId,
              role: 'user',
              text: composeComplaintPrompt(fields),
              viaVoice: false,
              createdAt: filedAt,
            },
            {
              id: asstTurnId,
              role: 'assistant',
              createdAt: filedAt,
              blocks: [
                {
                  kind: 'text',
                  text:
                    `✅ Filed quality issue **${issue.issue_no}** for ` +
                    `${fields.customer.trim()} — ${issueCategoryLabel(fields.issueCategory)} · ` +
                    `${severityLabel(fields.severity)}. Recorded as a structured quality issue ` +
                    `for pattern analysis; the team can review it.`,
                },
              ],
              usage: null,
              streaming: false,
              error: null,
            },
          ]);
          void loadList();
        } catch {
          chat.setError('Could not file the quality issue — please try again.');
        }
      });
    },
    [chat, loadList],
  );

  const hasConversation = chat.turns.length > 0;

  return (
    <div className={`${styles.page} ${embedded ? styles.pageEmbedded : ''}`}>
      <header className={styles.header}>
        <div className={styles.logo} aria-hidden="true">🎙️</div>
        <div className={styles.titleWrap}>
          <h1 className={styles.title}>Merlin</h1>
          <span className={styles.subtitle}>
            Quality &amp; Orders · record quality signals · check orders, customers &amp; products
          </span>
        </div>
        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.pill}
            onClick={() => setDrawerOpen(true)}
            title="Your conversation history"
            aria-haspopup="dialog"
          >
            📚 History
            {conversations.length > 0 && (
              <span className={styles.histCount}>{conversations.length}</span>
            )}
          </button>
          {invState.available && activeId && (
            chatCase ? (
              // The case this chat belongs to has ONE home now — its
              // workspace on the Cases tab (real URL, back button works).
              <Link
                className={styles.pill}
                to={`../cases/${encodeURIComponent(chatCase.investigation_no)}`}
                title={`This conversation is part of ${chatCase.investigation_no} — ${chatCase.title}. Open the case.`}
              >
                📁 part of {primaryRef(chatThread, chatCase).label} →
              </Link>
            ) : (
              <>
              {chatThread && (
                <Link
                  className={styles.pill}
                  to={`../threads/${encodeURIComponent(chatThread.thread_no)}`}
                  title={`This conversation is Merlin's thread ${chatThread.thread_no} — ${chatThread.title}. Open it.`}
                  data-testid="chat-thread-pill"
                  onClick={() =>
                    trackEvent('action', 'qcp_chat_thread_open', {
                      thread_no: chatThread.thread_no,
                      conversation_id: activeId,
                    })
                  }
                >
                  {primaryRef(chatThread, null).label}
                </Link>
              )}
              {!readOnly && (              <SaveAsInvestigationMenu
                conversationId={activeId}
                conversationTitle={conversationTitleFor(activeId)}
                turns={chat.turns}
                investigations={invState.investigations}
                threads={invState.threads}
                asks={dataRequests.requests}
                pillClassName={styles.pill}
                onSaved={(inv, outcome) => {
                  // Attaching to a case that already exists is not a new case
                  // (turn 3: "Saved as case INV-00004" read as a new one).
                  setSavedCase({ investigation: inv, outcome, conversationId: activeId });
                  setAskedToFill(false);
                  setConvoInvestigation(inv);
                  invState.applyUpdate(inv);
                }}
              />)}
              </>
            )
          )}
          <button
            type="button"
            className={styles.gearBtn}
            onClick={() => setHeaderOpen((o) => !o)}
            title="Voice &amp; model settings"
            aria-label="Voice and model settings"
            aria-expanded={headerOpen}
          >
            ⚙︎
          </button>
          <div className={`${styles.headerExtras} ${headerOpen ? styles.headerExtrasOpen : ''}`}>
            <button
              type="button"
              className={`${styles.pill} ${chat.autoplay ? '' : styles.pillOff}`}
              onClick={() => chat.setAutoplay(!chat.autoplay)}
              title="Auto-play spoken replies"
              aria-pressed={chat.autoplay}
            >
              <span className={styles.dot} />
              {chat.autoplay ? 'Auto-play on' : 'Auto-play off'}
            </button>
            <label className={styles.speed} title="Voice speed">
              <span aria-hidden="true">⏩</span>
              <input
                type="range"
                min={0.7}
                max={2}
                step={0.05}
                value={chat.speed}
                onChange={(e) => chat.setSpeed(parseFloat(e.target.value))}
                aria-label="Voice speed"
              />
              <span className={styles.speedVal}>{formatSpeed(chat.speed)}</span>
            </label>
            <select
              className={styles.voiceSelect}
              value={chat.voice}
              onChange={(e) => chat.setVoice(e.target.value)}
              title="Spoken voice"
              aria-label="Spoken voice"
            >
              {GCP_VOICES.map((v) => (
                <option key={v.name} value={v.name}>
                  {v.label}
                </option>
              ))}
            </select>
            {chat.models.length > 0 && (
              <ModelSelect
                models={chat.models}
                value={chat.selectedModelId || chat.defaultModel}
                onChange={chat.setSelectedModelId}
                disabled={chat.busy}
              />
            )}
          </div>
          {hasConversation && (
            <button type="button" className={styles.newBtn} onClick={startNew}>
              New
            </button>
          )}
        </div>
      </header>

      {reviewOrderId && (
        <Suspense fallback={null}>
          <OrderDetailModal
            isOpen
            orderId={reviewOrderId}
            onClose={() => setReviewOrderId(null)}
          />
        </Suspense>
      )}
      {dataRequests.available && unseenActiveRequest && (
        <AnswerReceivedBanner
          request={unseenActiveRequest}
          onOpenRequests={() => setAsksForced(true)}
          onRequestUpdated={dataRequests.applyUpdate}
        />
      )}
      <div className={styles.chatArea}>
        <div className={styles.chatMain}>
          <CopilotTurnList
            compactVoice={embedded}
            turns={chat.turns}
            loading={chat.loadingConvo}
            onReview={setReviewOrderId}
            onReplay={chat.replay}
            onStop={chat.stopSpeaking}
            speakingId={chat.speakingId}
            isSpeaking={chat.isSpeaking}
            isSynthesizing={chat.isSynthesizing}
            showUsage={isAdmin}
            conversationId={activeId}
            strongestModelId={chat.strongestModel || null}
            strongestModelLabel={
              chat.models.find((m) => m.id === chat.strongestModel)?.label ?? null
            }
            emptyState={
              <div className={styles.empty}>
                <div className={styles.spoken}>
                  <div className={styles.spokenHead}>
                    <span className={styles.spokenLabel}>Spoken</span>
                  </div>
                  <p className={styles.spokenText}>
                    Hi! I'm Merlin. Ask me to check an order, flag a quality issue,
                    or look into a customer or product — just talk to me.
                  </p>
                </div>
                <p className={styles.hello}>Try one of these, or tap the mic and speak:</p>
                <div className={styles.suggest}>
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s.label}
                      type="button"
                      className={styles.chip}
                      data-action="qcp_chat_starter_pick"
                      data-value={s.label}
                      onClick={() => {
                        trackEvent('action', 'qcp_chat_starter_pick', { label: s.label, result: 'text_filled' });
                        putStarter(s.starter);
                      }}
                      disabled={chat.busy}
                      title="Puts a start in the message box for you to finish"
                    >
                      <span className={styles.chipEmoji}>{s.emoji}</span>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            }
          />

          {chat.error && (
            <div className={styles.errorBar} role="alert">
              {chat.error}
            </div>
          )}

          {/* This conversation's own asks — where the question was asked,
              answer-first (Scott/Jess 2026-09-08: the drawer hid them). */}
          {dataRequests.available && conversationAsks.length > 0 && (
            <div className={styles.asksHost}>
              <AsksSection
                title="Asks in this conversation"
                requests={conversationAsks}
                onRequestUpdated={dataRequests.applyUpdate}
                defaultOpen={false}
                forceOpen={asksForced}
              />
            </div>
          )}

          {savedCase && savedCase.conversationId === activeId && (
            <SavedCaseNotice
              investigation={savedCase.investigation}
              conversationId={savedCase.conversationId}
              outcome={savedCase.outcome}
              caseHref={`../cases/${encodeURIComponent(savedCase.investigation.investigation_no)}`}
              // A case saved from a chat starts empty although Merlin has the
              // customer, product and order (turns 1 and 2): one click asks
              // him to fill it in from this conversation.
              fillOffered={!askedToFill}
              fillDisabled={chat.busy}
              onFill={() => {
                setAskedToFill(true);
                chat.sendText(fillCaseRequest(savedCase.investigation.investigation_no));
              }}
              onUndone={(inv) => {
                // Back off the case: the header offers Save again.
                invState.applyUpdate(inv);
                setConvoInvestigation(null);
                setSavedCase(null);
              }}
            />
          )}
          {/* Only before the first message: after it, the hint covered the
              bottom of Merlin's reply (turn 4). */}
          {reportIntent && !savedCase && !chatCase && chat.turns.length === 0 && (
            <p className={styles.reportHint} role="note">
              <b>Reporting a new issue:</b> tell Merlin who the customer is, which product and lot,
              and what happened. After you send your first message, a{' '}
              <b>Save as investigation</b> button appears at the top of this page: press it to
              put the problem on the Cases tab.
            </p>
          )}
          {SHOW_FORM_MODE && inputMode === 'form' ? (
            <div className={styles.formHost}>
              <QualityCopilotComplaintForm onSubmit={handleFormSubmit} busy={chat.busy} />
              <button
                type="button"
                className={styles.formToggle}
                onClick={() => setInputMode('voice')}
              >
                ⌨️ Back to chat
              </button>
            </div>
          ) : (
            <CopilotComposer
              inputRef={composerRef}
              value={chat.textValue}
              onChange={chat.setTextValue}
              onSubmit={chat.submitTyped}
              onAudio={(r) => void chat.handleAudio(r)}
              onStop={() => void chat.handleStop()}
              busy={chat.busy}
              transcribing={chat.transcribing}
              cancelling={chat.cancelling}
              attach={chat.attach}
              footNote={
                <>
                  Spoken rundown plays first · every reply is auditable
                  {SHOW_FORM_MODE && (
                    <>
                      {' · '}
                      <button
                        type="button"
                        className={styles.formToggle}
                        onClick={() => setInputMode('form')}
                      >
                        📋 Use the structured form
                      </button>
                    </>
                  )}
                </>
              }
              extra={
                isAdmin && chat.totals.turn_count > 0 ? (
                  <div
                    className={styles.totalsBadge}
                    title="Running total for this conversation (admin only)"
                  >
                    Σ {(chat.totals.input_tokens + chat.totals.output_tokens).toLocaleString()} tokens ·{' '}
                    {formatCostUsd(chat.totals.cost_usd)}
                  </div>
                ) : null
              }
            />
          )}
        </div>
      </div>

      <QcpConversationDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        conversations={conversations}
        activeId={activeId}
        loading={loadingList}
        ownerName={ownerName}
        ownerInitials={ownerInitials}
        onSelect={openConversation}
        onNew={startNew}
        onRename={(id, title) => void handleRename(id, title)}
      />
    </div>
  );
}
